import React, { useEffect, useState } from "react";
import {
  useAddPaymentMutation,
  useOrderByIdQuery,
  useOrdersQuery,
  usePaymentByIdQuery,
  useStatusQuery,
} from "../../Store/Apis/bookingApi";
import { updateReservation } from "../../Store/Apis/ReservationApi";
import Error from "../../Components/Error/Error";
import Loader from "../../Components/Loader/Loader";
import { Link, useLocation } from "react-router-dom";
import { useOutletsQuery } from "../../Store/Apis/outletApi";
import getStatusColor from "../../Hooks/GetStatusColor";
import {
  isDropdownPermission,
  isPermissionGranted,
} from "../../Hooks/permissionsMiddleware";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import AddPaymentForm from "../../Components/AddPaymentForm/AddPaymentForm";
import { useSelector } from "react-redux";
import formatDate from "../../Hooks/formatDate";
import { useRolesQuery } from "../../Store/Apis/rolePermissionApi";
import { OctagonAlert } from 'lucide-react';

const SalesMobileHistory = () => {
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const statusFromUrl = urlParams.get("status");
  const [sortFilter, setSortFilter] = useState({
    search: "",
    outlet: "",
    status: statusFromUrl || "",
    technicianID: "",
  });

  const roleIdFromUser = Number(
    user?.role_Id ??
      user?.roleId ??
      user?.roleID ??
      user?.role_id ??
      (typeof user?.role === "number" ? user?.role : NaN)
  );
  const roleNameFromUser = String(
    user?.role || user?.roleName || ""
  ).toLowerCase();
  const isCreditManager =
    roleIdFromUser === 51 ||
    roleNameFromUser === "credit manager" ||
    roleNameFromUser === "credit_manager" ||
    roleNameFromUser === "creditmanager";
  const isBarTenderRole = roleIdFromUser === 7;

  const bookingApi = useOrdersQuery({
    keyword: sortFilter.search,
    statusName: isBarTenderRole ? "Bar sales" : sortFilter.status,
    outletID: sortFilter.outlet,
    technicianID: sortFilter.technicianID,
    exceeded_limit: isCreditManager ? "yes" : "no",
  });

  const outletList = useOutletsQuery();
  const statusList = useStatusQuery();
  const roleApi = useRolesQuery();

  // ADD PAYMENT AND UPDATE ORDER
  const [paymentFormModal, setPaymentFormModal] = useState();
  const [addPaymentMutation] = useAddPaymentMutation();

  const orderByIdApi = useOrderByIdQuery(
    {
      keyID: paymentFormModal?.orderId,
    },
    { skip: !paymentFormModal?.orderId }
  );

  const paymentByIdApi = usePaymentByIdQuery(
    {
      orderId: paymentFormModal?.orderId,
    },
    { skip: !paymentFormModal?.orderId }
  );

  const handleAddPayment = (formData) => {
    console.log(formData);
    addPaymentMutation({
      paymentDate: new Date().toISOString(),
      customerId: orderByIdApi?.data?.data[0].customerID || 0,
      companyID: orderByIdApi?.data?.data[0].companyID || 0,
      customerName: orderByIdApi?.data?.data[0].customerName,
      orderId: paymentFormModal?.orderId,
      receivedAmount: formData.paidAmount,
      payAmount: formData.paidAmount,
      paymentType: formData.paymentType,
      paymentNote: formData.notes,
      utrNumber: formData.utrNumber,
    }).then((res) => {
      console.log(res);
      if (res?.data?.status === 200) {
        setPaymentFormModal(false);
        toast.success(res?.data?.message, { duration: 7000 });
      } else {
        toast.error(res?.data?.message, { duration: 7000 });
      }
    });
  };

  // Helper to detect exceeded limit across naming variants
  const hasExceededFlag = (o) => {
    const v1 = o?.exceeded_limit;
    const v2 = o?.exceed_Limit;
    const v3 = o?.exceededLimit;
    const val = v1 ?? v2 ?? v3;
    if (typeof val === "string") return val.toLowerCase() === "yes";
    if (typeof val === "number") return val === 1;
    if (typeof val === "boolean") return val === true;
    return false;
  };

  // Apply role-based visibility filtering client-side
  const visibleOrders = (bookingApi?.data?.data || []).filter((o) => {
    if (isCreditManager) {
      return hasExceededFlag(o);
    } else if (isBarTenderRole) {
      return (
        o.statusID === 12 ||
        o.status_id === 12 ||
        o.statusDescription === "Bar sales"
      );
    } else {
      return !hasExceededFlag(o);
    }
  });

  // Derive company booking dates
  const deriveCompanyDates = (order) => {
    const services = order?.services || order?.serviceTypeAndName || [];
    const ins = services
      .map((s) => s?.subCustomerCheckIn)
      .filter(Boolean)
      .map((d) => new Date(d));
    const outs = services
      .map((s) => s?.subCustomerCheckOut)
      .filter(Boolean)
      .map((d) => new Date(d));
    const minIn = ins.length ? new Date(Math.min(...ins)) : null;
    const maxOut = outs.length ? new Date(Math.max(...outs)) : null;
    return {
      checkIn: minIn ? minIn.toISOString() : order?.orderDate,
      checkOut: maxOut ? maxOut.toISOString() : order?.checkOutDate,
    };
  };

  // Transform data for mobile cards
  const transformedData = visibleOrders.map((elem, index) => {
    const isCompany = !!elem?.companyID;
    const { checkIn, checkOut } = isCompany
      ? deriveCompanyDates(elem)
      : { checkIn: elem?.orderDate, checkOut: elem?.checkOutDate };

    const rawSubTotal = Number(
      (elem.subTotal || 0) + (elem.taxAmount || 0) - (elem.discount || 0)
    );

    let creditedAggregate = 0;
    if (
      elem.creditedAmount !== null &&
      elem.creditedAmount !== undefined &&
      !isNaN(elem.creditedAmount)
    ) {
      creditedAggregate = Number(elem.creditedAmount);
    } else if (Array.isArray(elem.serviceTypeAndName)) {
      creditedAggregate = elem.serviceTypeAndName.reduce(
        (sum, s) => sum + (Number(s?.creditedAmount) || 0),
        0
      );
    }

    const netAmount = Math.max(0, rawSubTotal - creditedAggregate);

    return {
      key: index,
      orderNumber: elem.orderNumber,
      orderDate: checkIn,
      checkOutDate: checkOut,
      outletName: elem.outletName,
      outletId: elem.outletId ?? elem.outletID ?? user?.outletID,
      customerName: elem.customerName,
      customerNumber: elem.customerNumber,
      creditedAmount: creditedAggregate.toFixed(2),
      subTotal: rawSubTotal.toFixed(2),
      netAmount: netAmount.toFixed(2),
      statusDescription: elem.statusDescription,
      amountPaid: Number(elem.amountPaid).toFixed(2),
      totalAmount: Number(elem.totalAmount).toFixed(2),
      orderId: elem.orderId,
      createdBy: elem.createdBy,
      createdByName: elem.createdByName,
    };
  });

  useEffect(() => {
    if (isBarTenderRole) {
      setSortFilter({
        search: "",
        outlet: user.outletID || "",
        status: "Bar sales",
        technicianID: "",
      });
    } else if (user.role !== "superAdmin") {
      setSortFilter({
        ...sortFilter,
        outlet: user.outletID,
        status: statusFromUrl || sortFilter.status,
      });
    }
    if (roleApi.isSuccess && user.role === "Technician") {
      const technicianRole = roleApi.data?.data?.find(
        (elem) => elem.roleName === "Technician"
      );
      setSortFilter((prevFilter) => ({
        ...prevFilter,
        technicianID: technicianRole?.roleID === user.role_Id ? user.id : "",
        status: isBarTenderRole
          ? "Bar sales"
          : statusFromUrl || prevFilter.status,
      }));
    }
  }, [roleApi.isSuccess, statusFromUrl, isBarTenderRole]);

  const MobileOrderCard = ({ order }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <div className="card mb-3 shadow-sm">
        <div className="card-body p-3">
          {/* Header with Order ID and Status */}
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 className="mb-1 fw-bold text-primary">
                {order.orderNumber}
              </h6>
              <small className="text-muted">{order.outletName}</small>
            </div>
            <span
              className={`badge badge-${getStatusColor(
                order.statusDescription
              )} px-2 py-1`}
              style={{ fontSize: "0.75rem" }}
            >
              {order.statusDescription}
            </span>
          </div>

          {/* Customer Info */}
          <div className="mb-2">
            <div className="fw-bold text-dark">{order.customerName}</div>
            <small className="text-muted">{order.customerNumber}</small>
          </div>

          {/* Dates */}
          <div className="row mb-2">
            <div className="col-6">
              <small className="text-muted d-block">Check In</small>
              <small className="fw-bold">{formatDate(order.orderDate)}</small>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Check Out</small>
              <small className="fw-bold">
                {formatDate(order.checkOutDate)}
              </small>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-light p-2 rounded mb-2">
            <div className="row">
              <div className="col-6">
                <small className="text-muted d-block">Total Amount</small>
                <span className="fw-bold text-success">
                  R {order.netAmount}
                </span>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Paid Amount</small>
                <span className="fw-bold">R {order.amountPaid}</span>
              </div>
            </div>
            {Number(order.creditedAmount) > 0 && (
              <div className="mt-1">
                <small className="text-muted">Credited: </small>
                <small className="text-danger fw-bold">
                  -R {order.creditedAmount}
                </small>
              </div>
            )}
          </div>

          {/* Expandable Details */}
          {expanded && (
            <div className="border-top pt-2 mt-2">
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">Order Amount: </small>
                  <span className="fw-bold">R {order.subTotal}</span>
                </div>
                <div className="col-12 mb-2">
                  <small className="text-muted">Created By: </small>
                  <span>{order.createdByName}</span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="d-flex flex-wrap gap-2 mt-2">
            <button
              className="btn btn-link p-0 text-primary"
              onClick={() => setExpanded(!expanded)}
              style={{ fontSize: "0.8rem" }}
            >
              {expanded ? "Less Details" : "More Details"}
            </button>

            {isPermissionGranted(2) && (
              <Link
                to={`/sales-history/details?id=${order.orderId}`}
                className="btn btn-outline-primary btn-sm"
                style={{ fontSize: "0.75rem" }}
              >
                View
              </Link>
            )}

            {isPermissionGranted(1) &&
              order.statusDescription !== "Completed" &&
              (() => {
                if (order.createdBy.toString() !== "0") {
                  return order.createdBy.toString() === user.id.toString();
                }
                const userRoleName = String(
                  user?.role || user?.roleName || ""
                ).toLowerCase();
                return userRoleName === "reservationist";
              })() && (
                <Link
                  to={`/sales-history/edit?id=${order.orderId}`}
                  className="btn btn-outline-warning btn-sm"
                  style={{ fontSize: "0.75rem" }}
                >
                  <i className="fas fa-edit me-1"></i>Edit
                </Link>
              )}

            {isPermissionGranted(2) &&
              order.statusDescription === "Checked In" && (
                <Link
                  to={`/sales-history/editCharges?id=${order.orderId}&outletID=${order.outletId}`}
                  className="btn btn-outline-info btn-sm"
                  style={{ fontSize: "0.75rem" }}
                >
                  <i className="fas fa-calculator me-1"></i>Charges
                </Link>
              )}

            {isPermissionGranted(5) && order.netAmount !== order.amountPaid && (
              <button
                className="btn btn-success btn-sm"
                onClick={() => setPaymentFormModal(order)}
                style={{ fontSize: "0.75rem" }}
              >
                <i className="fas fa-plus me-1"></i>Add Payment
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid px-2">
      {/* Header */}
      <div className="row align-items-center justify-content-between mb-3">
        <div className="col">
          <h5 className="fw-500 text-dark mb-0">Sales History</h5>
        </div>
      </div>

      {/* Filters - Mobile Optimized */}
      <div className="card mb-3">
        <div className="card-body p-3">
          {/* Search Bar */}
          <div className="mb-3">
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="Search by Order ID"
              value={sortFilter?.search || ""}
              
              onChange={(e) =>
                setSortFilter({ ...sortFilter, search: e.target.value })
              }
            />
          </div>

          {/* Filter Row */}
          <div className="row g-2">
            <div className="col-6">
              <select
                className="form-select"
                disabled={
                  !outletList.isSuccess ||
                  user.role_Id !== 10 ||
                  isBarTenderRole
                }
                value={sortFilter?.outlet}
                onChange={(e) =>
                  setSortFilter({ ...sortFilter, outlet: e.target.value })
                }
              >
                <option value="">All Outlets</option>
                {outletList?.data?.data.map((elem) => (
                  <option key={elem.outletId} value={elem.outletId}>
                    {elem.outletName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6">
              <select
                className="form-select"
                value={sortFilter?.status || ""}
                disabled={isBarTenderRole}
                onChange={(e) =>
                  setSortFilter({ ...sortFilter, status: e.target.value })
                }
              >
                {isBarTenderRole ? (
                  <option value="Bar sales">Bar Sales</option>
                ) : (
                  <>
                    <option value="">All Bookings</option>
                    {statusList?.data?.data.map((elem, i) => (
                      <option key={i} value={elem.description}>
                        {elem.description}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      {bookingApi.isSuccess && (
        <div className="mb-2">
          <small className="text-muted">
            Showing {transformedData.length} result
            {transformedData.length !== 1 ? "s" : ""}
          </small>
        </div>
      )}

      {/* Loading and Error States */}
      {bookingApi.isError && <Error />}
      {bookingApi.isLoading && <Loader height="300px" />}

      {/* Orders List */}
      {bookingApi.isSuccess && (
        <div className="pb-4">
          {transformedData.length === 0 ? (
            <div className="text-center py-5">
                <OctagonAlert size={48} className="text-muted mb-3" />
              <h6 className="text-muted">No sales found</h6>
              <p className="text-muted small">
                Try adjusting your search filters
              </p>
            </div>
          ) : (
            transformedData.map((order) => (
              <MobileOrderCard key={order.key} order={order} />
            ))
          )}
        </div>
      )}

      {/* PAYMENT FORM MODAL */}
      <Modal
        show={paymentFormModal}
        onHide={() => setPaymentFormModal(null)}
        size="lg"
        centered
      >
        <Modal.Body>
          <h4 className="mb-4 fs-5">Payment Details</h4>
          {((orderByIdApi.isLoading && paymentByIdApi.isLoading) ||
            (orderByIdApi.isFetching && paymentByIdApi.isFetching)) && (
            <Loader height={500} />
          )}
          {orderByIdApi.isSuccess &&
            paymentByIdApi.isSuccess &&
            !orderByIdApi.isFetching &&
            !paymentByIdApi.isFetching && (
              <AddPaymentForm
                handleSubmit={handleAddPayment}
                totalAmount={orderByIdApi?.data?.data[0].subTotal}
                setPaymentFormModal={setPaymentFormModal}
                data={{
                  discountType: paymentFormModal?.discountType,
                  discount: orderByIdApi?.data?.data[0].discount,
                  paidAmount: orderByIdApi?.data?.data[0].amountPaid,
                  paymentType: paymentByIdApi?.data?.data[0].paymentType,
                  utrNumber: paymentByIdApi?.data?.data[0].utrNumber,
                  notes: paymentByIdApi?.data?.data[0].notes,
                  specialInstruction: "",
                  orderId: orderByIdApi?.data?.data[0].orderId,
                  paidAmount: paymentByIdApi?.data?.data.reduce(
                    (acc, payment) => acc + payment.amount,
                    0
                  ),
                }}
              />
            )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SalesMobileHistory;
